import React, { useCallback, useMemo, useState } from "react"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { useApiQueryTable } from "../../../../../../core/api/components/ApiQueryTable"
import { MRT_ColumnDef, MRT_Row } from "material-react-table"
import { MenuItem } from "@mui/material"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { SuccessDisplay } from "../../../../../../core/api/errorTypes"
import { ConfirmModal } from "../../../../../../shared/components/dialogs/ConfirmModal"
import { getEditTermsAndConditionRoute } from "../../../../../../constants/routes"
import { useNavigate } from "react-router"
import { TermsAndConditionsItem } from "../termsAndConditionTypes"
import {
    deleteTermsAndConditions,
    FetchSupplierTermsAndConditionsResponse,
    fetchTermsAndConditions,
    TermsAndConditionQuery,
} from "../termsAndConditionApi"
import { useCurrentSupplier } from "../../../../../suppliers/supplierHooks"
function transformData(data: FetchSupplierTermsAndConditionsResponse) {
    return data.termsAndConditions
}

export function TermsAndConditionsTable() {
    const navigate = useNavigate()

    const { l10n } = useAppLocalization()
    const [showDeleteDialog, setShowDeleteDialog] = useState(false)
    const { supplierId } = useCurrentSupplier()
    const [termsAndCondition, setTermsAndCondition] = useState<TermsAndConditionsItem>()

    const supplierTermsAndConditions = useApiQuery(fetchTermsAndConditions, {
        queryName: TermsAndConditionQuery.LIST,
        dependencies: {
            supplierId,
        },
        select: transformData,
    })

    const { mutate: callDeleteTermsAndConditions } = useApiMutation(deleteTermsAndConditions, {
        successDisplay: SuccessDisplay.SNACKBAR,
        async onSuccess(_, data, context) {
            await context.queryClient.invalidateQueries({ queryKey: [TermsAndConditionQuery.LIST] })
        },
    })

    const handleDeleteClick = () => {
        if (!termsAndCondition?.id) return
        callDeleteTermsAndConditions({ termsAndConditionsId: termsAndCondition.id })
    }

    const { ApiQueryTable, columnFactory } = useApiQueryTable(supplierTermsAndConditions)

    const columns = useMemo<MRT_ColumnDef<TermsAndConditionsItem>[]>(
        () => [
            columnFactory.nameColumn({
                header: l10n.getString("terms-and-conditions-table-header-name"),
                accessorKey: "callName",
            }),
            columnFactory.typeColumn({
                header: l10n.getString("terms-and-conditions-table-header-name-type"),
                accessorKey: "type",
            }),
            columnFactory.typeColumn({
                header: l10n.getString("terms-and-conditions-table-header-language"),
                accessorKey: "languageCode",
                accessorFn: (row) => row?.languageCode.toUpperCase(),
            }),
            columnFactory.activeInactiveStatusColumn(
                {
                    header: l10n.getString("terms-and-conditions-table-header-name-status"),
                },
                (row) => row.isActive,
            ),
        ],
        [l10n, columnFactory],
    )

    const renderRowActionMenuItems = useCallback(
        ({ row, closeMenu }: { row: MRT_Row<TermsAndConditionsItem>; closeMenu: () => void }) => [
            <MenuItem
                key={`edit-${row.original.id}`}
                onClick={() => {
                    navigate(getEditTermsAndConditionRoute(row.original.id))
                    closeMenu()
                }}
            >
                <LocalizedStrict id="terms-and-conditions-table-button-edit">
                    <span>Edit</span>
                </LocalizedStrict>
            </MenuItem>,
            <MenuItem
                key={`delete-${row.original.id}`}
                onClick={() => {
                    closeMenu()
                    setTermsAndCondition(row.original)
                    setShowDeleteDialog(true)
                }}
            >
                <LocalizedStrict id="terms-and-conditions-table-button-delete">
                    <span>Delete</span>
                </LocalizedStrict>
            </MenuItem>,
        ],
        [l10n],
    )

    return (
        <>
            <ApiQueryTable
                columns={columns}
                initialState={{ density: "spacious" }}
                renderRowActionMenuItems={renderRowActionMenuItems}
            />

            <ConfirmModal
                buttonLabelSubmit={l10n.getString("terms-and-conditions-modal-button-delete-label")}
                title={l10n.getString("terms-and-conditions-modal-title")}
                message={`${l10n.getString("terms-and-conditions-modal-question")} ${termsAndCondition?.callName}?`}
                handleClick={handleDeleteClick}
                open={showDeleteDialog}
                onClose={() => setShowDeleteDialog(false)}
            />
        </>
    )
}
